/**
 * Sync Manager Service
 * Handles data synchronization between offline storage and Firebase
 */

import { Subscription } from 'rxjs';
import { setData, updateData, removeData } from '../firebase/api-wrapper.service';
import offlineStorage from './storage.service';
import networkMonitor from './network-monitor.service';
import logger from '../../utils/logger';
import { store } from '../../store';
import { setSyncStatus, setPendingSyncCount } from '../../store/slices/networkSlice';

interface SyncQueueItem {
  id?: number;
  type: string;
  action: 'save' | 'update' | 'delete';
  data: any;
  timestamp: number;
  retryCount?: number;
}

class SyncManagerService {
  private isSyncing = false;
  private syncInterval: number | null = null;
  private networkSubscription: Subscription | null = null;
  private readonly MAX_RETRIES = 3;
  private readonly SYNC_INTERVAL = 30000; // 30 seconds

  /**
   * Start automatic sync
   */
  startAutoSync(): void {
    if (this.syncInterval) return;

    // Initial sync if online
    if (networkMonitor.isOnline) {
      this.syncPendingChanges();
    }

    // Set up periodic sync
    this.syncInterval = window.setInterval(() => {
      if (networkMonitor.canSync && !this.isSyncing) {
        this.syncPendingChanges();
      }
    }, this.SYNC_INTERVAL);

    // Clean up existing subscription if any
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }

    // Listen for network changes and store the subscription
    this.networkSubscription = networkMonitor.state$.subscribe((state) => {
      if (state.connected && !this.isSyncing) {
        // Sync when coming back online
        setTimeout(() => this.syncPendingChanges(), 2000);
      }
    });

    logger.info('Auto sync started');
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    // Unsubscribe from network state changes
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
      this.networkSubscription = null;
    }

    logger.info('Auto sync stopped');
  }

  /**
   * Sync all pending changes
   */
  async syncPendingChanges(): Promise<void> {
    if (this.isSyncing || !networkMonitor.isOnline) return;

    this.isSyncing = true;
    store.dispatch(setSyncStatus({ isSyncing: true, progress: 0 }));

    try {
      const queue = await offlineStorage.getSyncQueue();
      const total = queue.length;

      if (total === 0) {
        logger.info('No pending changes to sync');
        return;
      }

      logger.info(`Starting sync of ${total} pending changes`);

      let processed = 0;
      const errors: SyncQueueItem[] = [];

      for (const item of queue) {
        try {
          await this.syncItem(item);
          
          // Remove from queue
          if (item.id !== undefined) {
            await offlineStorage.removeFromSyncQueue(item.id);
          }

          processed++;
          const progress = Math.round((processed / total) * 100);
          store.dispatch(setSyncStatus({ isSyncing: true, progress }));
        } catch (error) {
          logger.error(`Failed to sync item:`, error);
          
          // Increment retry count
          const retryCount = (item.retryCount || 0) + 1;
          
          if (retryCount < this.MAX_RETRIES) {
            // Update retry count in queue
            errors.push({ ...item, retryCount });
          } else {
            logger.error(`Max retries reached for item, removing from queue:`, item);
            if (item.id !== undefined) {
              await offlineStorage.removeFromSyncQueue(item.id);
            }
          }
        }
      }

      // Re-add items that failed with updated retry count
      for (const error of errors) {
        await offlineStorage.addToSyncQueue(error.type, error.action, error.data);
      }

      const remaining = await offlineStorage.getSyncQueue();
      store.dispatch(setPendingSyncCount(remaining.length));

      logger.info(`Sync completed: ${processed}/${total} successful`);
    } catch (error) {
      logger.error('Sync failed:', error);
    } finally {
      this.isSyncing = false;
      store.dispatch(setSyncStatus({ isSyncing: false, progress: 0 }));
    }
  }

  /**
   * Sync a single item
   */
  private async syncItem(item: SyncQueueItem): Promise<void> {
    const { type, action, data } = item;

    switch (type) {
      case 'profiles':
        await this.syncProfile(action, data);
        break;
      
      case 'trainings':
        await this.syncTraining(action, data);
        break;
      
      case 'goals':
        await this.syncGoal(action, data);
        break;
      
      case 'workouts':
        await this.syncWorkout(action, data);
        break;
      
      default:
        logger.warn(`Unknown sync type: ${type}`);
    }
  }

  /**
   * Sync profile changes
   */
  private async syncProfile(action: string, data: any): Promise<void> {
    const { uid, data: profileData } = data;
    
    switch (action) {
      case 'update':
        await updateData(`/users/${uid}`, profileData);
        break;
      default:
        throw new Error(`Unknown profile action: ${action}`);
    }
  }

  /**
   * Sync training changes
   */
  private async syncTraining(action: string, data: any): Promise<void> {
    switch (action) {
      case 'save':
        await setData(`/trainings/${data.id}`, data);
        break;
      case 'update':
        await updateData(`/trainings/${data.id}`, data);
        break;
      case 'delete':
        await removeData(`/trainings/${data.id}`);
        break;
      default:
        throw new Error(`Unknown training action: ${action}`);
    }
  }

  /**
   * Sync goal changes
   */
  private async syncGoal(action: string, data: any): Promise<void> {
    const { uid, goalId, goalData } = data;
    
    switch (action) {
      case 'save':
      case 'update':
        await setData(`/goals/private/${uid}/${goalId}`, goalData);
        break;
      case 'delete':
        await removeData(`/goals/private/${uid}/${goalId}`);
        break;
      default:
        throw new Error(`Unknown goal action: ${action}`);
    }
  }

  /**
   * Sync workout changes
   */
  private async syncWorkout(action: string, data: any): Promise<void> {
    const { uid, routineId, routineData } = data;
    
    switch (action) {
      case 'save':
      case 'update':
        await setData(`/workoutRoutines/${uid}/${routineId}`, routineData);
        break;
      case 'delete':
        await removeData(`/workoutRoutines/${uid}/${routineId}`);
        break;
      default:
        throw new Error(`Unknown workout action: ${action}`);
    }
  }

  /**
   * Force sync now
   */
  async syncNow(): Promise<void> {
    if (!networkMonitor.isOnline) {
      throw new Error('Cannot sync while offline');
    }

    await this.syncPendingChanges();
  }

  /**
   * Get sync status
   */
  getSyncStatus(): { isSyncing: boolean; pendingCount: number } {
    const state = store.getState();
    return {
      isSyncing: state.network.isSyncing,
      pendingCount: state.network.pendingSyncCount
    };
  }
}

// Export singleton instance
const syncManagerService = new SyncManagerService();
export default syncManagerService;